/**
 * "행/항목을 추가하면 **방금 만든 것에 바로 입력할 수 있어야 한다**" — 그 규칙의 공용 부속.
 *
 * 목록형(TextareaListField)은 새 항목 컨트롤을 자기가 렌더하므로 ref 콜백(focusOnMount)으로
 * 포커스를 준다. 반면 표 위젯(renderEditableCell)과 행 그리드(RowFieldControl)는 컨트롤을
 * 위젯 종류별로 분기 렌더해 ref를 꿸 자리가 없다 — 그래서 추가 직후 **DOM에서 그 칸을 찾아**
 * 포커스한다(값·상태는 건드리지 않는다: 포커스는 표시 계층의 일이다).
 *
 * 두 갈래로 나눠 둔다.
 *  - `firstTypeableCellKey`(순수): 방금 만든 행에서 **어느 칸이 첫 입력 대상인가**. 단위 테스트 대상.
 *  - `focusAddedTableCell`/`focusAddedGridField`(DOM): 그 칸의 입력 컨트롤에 포커스 + 커서 끝.
 *
 * ⚠️ 이 모듈은 렌더 중에 호출되지 않는다(클릭 핸들러 전용). SSR(renderToStaticMarkup) 경로에
 * 섞이지 않도록 window/document 접근은 전부 가드 뒤에 둔다.
 */

/**
 * 포커스를 줄 수 있는 셀 위젯 — **글자를 칠 수 있는 칸**만이다.
 *
 * 선택 전용 칸(suggestionsFrom.requireSelection)도 포함한다: 친 글자가 값으로 커밋되지 않을 뿐
 * 화면에는 그대로 보이고(검색어 초안 draftValue) 그 타이핑이 곧 제안 드롭다운을 연다 —
 * "검색해서 고르는" 진입점이 바로 이 칸이다.
 *
 * 선택형(checkbox/radio/select)은 **제외**한다. 포커스 링만 생기고 타이핑은 아무 일도 하지 않아
 * "커서는 갔는데 안 써지는" 상태가 되는데, 그건 지금(포커스 없음)보다 나을 게 없다. 그런 표는
 * 종전대로 포커스 없이 추가만 된다(회귀 0).
 */
const TYPEABLE_CELL_WIDGETS: ReadonlySet<string> = new Set(['text', 'textarea', 'number']);

/** 첫 입력 대상 판정 입력 — 표 칼럼/행 그리드 필드를 이 형상으로 정규화해서 넘긴다. */
export interface AddedRowCell {
  /** 칸 식별자(표=DataDefField.key, 행 그리드=DataSourceRowField.field). */
  key: string;
  /** 셀 컨트롤 종류(미지정=text — 두 경로 모두 기본이 텍스트 입력이다). */
  widget?: string;
  /** 이 행에서 잠긴 칸(표 readonly·readonlyFields 잠금·필드 editable:false). */
  locked?: boolean;
  /**
   * 선택 전용인데 호스트 검색이 미배선이라 읽기 전용으로 degrade된 칸 — 값을 만들 방법 자체가
   * 없으므로 포커스 대상이 아니다(그 칸은 readOnly 컨트롤로 렌더된다).
   */
  unavailable?: boolean;
}

/**
 * 방금 추가한 행에서 커서를 놓을 첫 칸 — **표시 순서상 처음** 나오는 입력 가능 칸이다.
 * 대상이 없으면 undefined(포커스하지 않는다).
 */
export function firstTypeableCellKey(cells: AddedRowCell[]): string | undefined {
  const hit = cells.find(
    (c) =>
      c.locked !== true &&
      c.unavailable !== true &&
      TYPEABLE_CELL_WIDGETS.has(c.widget ?? 'text'),
  );
  return hit?.key;
}

// ── DOM 부속 ────────────────────────────────────────────────────────────────
//
// ⚠️ 아래 앵커 이름은 **JSX 쪽에 리터럴로 적혀 있다**(composerWidgetTreeRender의 표 셀·래퍼,
// RowsCollectionEditor의 행 카드·필드 블록). 이름을 바꾸면 그 리터럴도 같이 고칠 것 —
// 어긋나면 조회가 조용히 null이 되어 포커스만 사라진다(에러가 나지 않는다).

/** 표 위젯 인스턴스 경계 — 반복(repeat) 안에서는 같은 node.id가 여러 번 그려지므로 필수 앵커. */
export const TABLE_WIDGET_ATTR = 'data-table-widget';
/** 표 셀의 행 앵커(이미 편집 종료 판정이 쓰고 있다 — 같은 값을 재사용한다). */
export const TABLE_ROW_INDEX_ATTR = 'data-table-row-index';
/** 표 셀의 칼럼 앵커. */
export const TABLE_CELL_FIELD_ATTR = 'data-table-cell-field';
/** 행 그리드 카드의 행 앵커(rowId). */
export const ROW_CARD_ID_ATTR = 'data-row-card-id';
/** 행 그리드 필드 블록의 칸 앵커. */
export const ROW_FIELD_ATTR = 'data-row-field';
/**
 * 반복(repeat) 행 인스턴스 컨테이너 앵커 — 카드형·민짜(chrome:'none')·표형 세 렌더 경로 공용.
 * 값은 행의 `rowId`(추가 행은 `createBlankRow`가 넣는 uuid라 전역 유일).
 */
export const REPEAT_ROW_ID_ATTR = 'data-repeat-row-id';
/**
 * formTable 반복 행 `<tr>` 앵커 — 이쪽 행에는 rowId가 없어(값이 평범한 객체 배열) **위치**로
 * 식별한다. 값은 `${반복 행 정의 id}::${데이터 인덱스}` — 한 표에 반복 블록이 여럿일 수 있어
 * 인덱스만으로는 갈린다.
 */
export const FT_REPEAT_ROW_ATTR = 'data-ft-repeat-row';
/** 목록형(textarea-list/text-list) 항목 래퍼 앵커 — 값은 항목 인덱스. */
export const LIST_ITEM_INDEX_ATTR = 'data-list-item-index';

/**
 * `TYPEABLE_CELL_WIDGETS`의 **DOM 판** — 위젯 선언이 아니라 *이미 그려진 컨트롤*을 보고 판정한다.
 *
 * 왜 두 판이 필요한가: 표 위젯은 칼럼 스키마가 있어 "몇 번째 칼럼"을 선언으로 고를 수 있지만,
 * 반복(repeat) 행과 목록 항목은 **임의 위젯 트리**라 셀 수 있는 칼럼이 없다. 그쪽은 새로 생긴 행
 * 컨테이너를 통째로 훑어 첫 입력 컨트롤을 집는 편이 위젯 구성 변화에 강하다.
 *
 * 두 집합은 같은 질문("이 칸에 글자를 칠 수 있나")의 선언판·DOM판이므로 **한쪽만 넓히지 말 것**.
 * rc가 실제로 그리는 input type은 text/number/checkbox/radio/date/time/file뿐이라(2026-08-24
 * 전수 확인) 여기서 걸러지는 건 선택형(checkbox/radio/select)·사진(file)·날짜/시각 picker다 —
 * 위 TYPEABLE_CELL_WIDGETS가 text/textarea/number만 고르는 것과 정확히 같은 경계다.
 */
const TYPEABLE_INPUT_TYPES: ReadonlySet<string> = new Set(['text', 'number']);

/**
 * 이 컨트롤에 커서를 두면 **바로 타이핑이 되는가**(순수 판정 — 단위 테스트 대상).
 * readonly/disabled는 값만 보여 주는 칸이라 제외하고, 선택형·파일·picker도 제외한다
 * ("커서는 갔는데 안 써지는" 상태는 포커스 없음보다 나을 게 없다는 기존 규약).
 */
export function isTypeableControl(el: Element | null | undefined): boolean {
  if (!el) return false;
  const tag = el.tagName.toLowerCase();
  if (tag !== 'input' && tag !== 'textarea') return false;
  if (el.hasAttribute('readonly') || el.hasAttribute('disabled')) return false;
  // 숨김 컨트롤(hidden 속성/aria-hidden 서브트리)은 focus()가 조용히 무시된다 — 미리 걸러
  // 다음 후보로 넘어가게 한다(그러지 않으면 "첫 컨트롤"에서 멈춰 포커스가 사라진다).
  if (el.hasAttribute('hidden') || el.closest('[aria-hidden="true"]') !== null) return false;
  if (tag === 'textarea') return true;
  // type 미지정 input의 기본은 text(HTML 규약).
  return TYPEABLE_INPUT_TYPES.has((el.getAttribute('type') ?? 'text').toLowerCase());
}

/**
 * 컨테이너 안 **표시 순서상 첫** 입력 컨트롤(없으면 null). 순수 조회 — 포커스는 주지 않는다.
 * `querySelectorAll`은 문서 순서를 보장하므로 "먼저 보이는 칸"과 일치한다.
 */
export function firstTypeableControlIn(
  container: Element | null | undefined,
): HTMLInputElement | HTMLTextAreaElement | null {
  if (!container) return null;
  const hit = Array.from(container.querySelectorAll('input, textarea')).find(isTypeableControl);
  return (hit as HTMLInputElement | HTMLTextAreaElement | undefined) ?? null;
}

/** 컨테이너 안 첫 입력 컨트롤에 포커스 + 커서 끝. 대상이 없거나 포커스가 안 걸리면 false. */
export function focusTextControlIn(container: Element | null | undefined): boolean {
  const el = firstTypeableControlIn(container);
  if (!el) return false;
  el.focus();
  // 숨김/미배치 컨트롤은 focus()가 조용히 무시된다 — 실제로 옮겨갔을 때만 성공으로 본다.
  if (typeof document !== 'undefined' && document.activeElement !== el) return false;
  const len = el.value.length;
  try {
    el.setSelectionRange(len, len);
  } catch {
    /* selection 미지원 입력 타입(number 등) — 포커스만으로 충분 */
  }
  return true;
}

/**
 * root 안에서 (행 앵커, 칸 앵커)가 모두 맞는 요소를 찾는다.
 * 표는 두 앵커가 같은 `<td>`에 있고(closest는 자기 자신을 포함하므로 그대로 맞는다), 행 그리드는
 * 칸 블록이 행 카드 **안에** 있다 — 한 함수가 두 형상을 같이 다룬다.
 */
export function findAnchoredCell(
  root: Element | null | undefined,
  rowAttr: string,
  rowValue: string,
  cellAttr: string,
  cellValue: string,
): Element | null {
  if (!root) return null;
  const candidates = Array.from(root.querySelectorAll(`[${cellAttr}]`));
  for (const el of candidates) {
    if (el.getAttribute(cellAttr) !== cellValue) continue;
    if (el.closest(`[${rowAttr}]`)?.getAttribute(rowAttr) === rowValue) return el;
  }
  return null;
}

/** 추가 직후 재시도 횟수 — 한 프레임 안에 커밋되는 게 보통이라 여유분만 둔다. */
export const ADDED_ROW_FOCUS_ATTEMPTS = 3;

/**
 * 다음 렌더 이후 대상 칸에 포커스. **첫 시도부터 미룬다** — 호출 시점(onChange 직후)의 DOM은
 * 아직 추가 전이고, 취소된 추가 행이 걷혀 인덱스가 재사용된 경우 옛 행에 잘못 포커스가 갈 수 있다.
 */
export function focusAfterRender(
  resolve: () => Element | null | undefined,
  attempts: number = ADDED_ROW_FOCUS_ATTEMPTS,
): void {
  if (typeof window === 'undefined') return;
  const schedule = (fn: () => void): void => {
    if (typeof window.requestAnimationFrame === 'function') window.requestAnimationFrame(() => fn());
    else setTimeout(fn, 0);
  };
  let left = Math.max(1, attempts);
  const tick = (): void => {
    left -= 1;
    if (focusTextControlIn(resolve())) return;
    if (left > 0) schedule(tick);
  };
  schedule(tick);
}

/**
 * 표 위젯: 방금 추가한 행(listIdx)의 칼럼(fieldKey) 셀로 커서.
 * `root`는 클릭한 추가 버튼에서 올라간 **그 표 인스턴스**여야 한다(반복 행마다 같은 node.id로
 * 여러 벌 그려지므로 document 전역 조회는 엉뚱한 발견사항의 표를 잡는다).
 */
export function focusAddedTableCell(
  root: Element | null | undefined,
  listIdx: number,
  fieldKey: string,
): void {
  focusAfterRender(() =>
    findAnchoredCell(
      root,
      TABLE_ROW_INDEX_ATTR,
      String(listIdx),
      TABLE_CELL_FIELD_ATTR,
      fieldKey,
    ),
  );
}

/** 행 그리드: 방금 추가한 행 카드(rowId)의 필드(field) 블록으로 커서. */
export function focusAddedGridField(
  root: Element | null | undefined,
  rowId: string,
  field: string,
): void {
  focusAfterRender(() =>
    findAnchoredCell(root, ROW_CARD_ID_ATTR, rowId, ROW_FIELD_ATTR, field),
  );
}

/**
 * root 안에서 `attr` 값이 `value`인 첫 요소. **셀렉터를 문자열로 조립하지 않는다** —
 * 앵커 값이 uuid/합성 키라 따옴표·특수문자 이스케이프를 신경 쓸 필요가 없어야 한다.
 */
export function findAnchored(
  root: Element | null | undefined,
  attr: string,
  value: string,
): Element | null {
  if (!root) return null;
  const hit = Array.from(root.querySelectorAll(`[${attr}]`)).find(
    (el) => el.getAttribute(attr) === value,
  );
  return hit ?? null;
}

/**
 * 반복(repeat)·formTable 반복·목록 공용: 방금 만든 **행/항목 컨테이너**의 첫 입력 컨트롤로 커서.
 *
 * 표 위젯(focusAddedTableCell)과 달리 "어느 칸"을 선언에서 고르지 않는다 — 행 안이 임의 위젯
 * 트리라 컨테이너를 통째로 훑는 편(firstTypeableControlIn)이 위젯 구성 변화에 강하기 때문이다.
 * `root`는 클릭한 추가 버튼이 속한 **그 인스턴스**여야 한다(중첩 반복·모달에서 같은 위젯 id가
 * 여러 벌 그려지므로 document 전역 조회는 남의 행을 잡는다).
 */
export function focusAddedRowContainer(
  root: Element | null | undefined,
  attr: string,
  value: string,
): void {
  focusAfterRender(() => findAnchored(root, attr, value));
}
